import { SyntheticEvent, useRef, useState } from "react"

import {
  selectAuthFlowEmail,
  selectAuthFlowError,
  selectAuthFlowLoading,
  selectLastUsedProvider,
  setAuthFlowEmail,
} from "@/lib/storage/auth-slice"
import { handleEmailStepSubmit } from "@/lib/storage/auth-thunks"
import { useAppDispatch, useAppSelector } from "@/lib/storage/hooks"
import { AuthProviderButtons } from "@/components/auth/auth-provider-buttons"
import { OrgNewsletterCheckbox } from "@/components/auth/org-newsletter-checkbox"
import { RegistrationTermsDisclaimer } from "@/components/auth/registration-terms-disclaimer"
import { Badge } from "@/components/ui/badge"
import { Button } from "../ui/button"
import { Form, FormControl, FormError, FormField } from "../ui/form"
import { TextInput } from "../ui/text-input"

export default function AuthStepEmail() {
  const dispatch = useAppDispatch()
  const email = useAppSelector(selectAuthFlowEmail)
  const loading = useAppSelector(selectAuthFlowLoading)
  const error = useAppSelector(selectAuthFlowError)
  const emailInputRef = useRef<HTMLInputElement | null>(null)
  const lastUsedProvider = useAppSelector(selectLastUsedProvider)
  // The field takes focus only when the user signed in by email last time,
  // since they may pick Google or Apple instead. In a drawer the dialog
  // focuses it once open, with the keyboard the auth dialog summoned.
  const doAutoFocus = lastUsedProvider === "email"
  const [emailFocused, setEmailFocused] = useState(false)
  const showDisclaimer = emailFocused || email !== ""

  const handleSubmit = async (e: SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault()
    void dispatch(handleEmailStepSubmit())
  }

  return (
    <>
      <Form onSubmit={handleSubmit}>
        <FormField name="email">
          <FormControl
            render={
              <TextInput
                type="email"
                autoComplete="username"
                placeholder="Email"
                required
                size="lg"
                value={email}
                onChange={(e) => dispatch(setAuthFlowEmail(e.target.value))}
                onFocus={() => setEmailFocused(true)}
                onBlur={() => setEmailFocused(false)}
                disabled={loading}
                autoFocus={doAutoFocus}
                ref={emailInputRef}
              />
            }
          />
          {lastUsedProvider === "email" ? (
            <Badge
              color="gray"
              size={1}
              className="pointer-events-none absolute -top-2.5 right-2"
            >
              Last used 👈
            </Badge>
          ) : null}
          {error && <FormError>{error}</FormError>}
        </FormField>
        <Button
          type="submit"
          variant="primary"
          block
          size="lg"
          loading={loading}
        >
          Continue
        </Button>
        {showDisclaimer && (
          // Keeps focus in the field, so the links don't move out from under
          // a click on an empty field. -mt-1 matches the phone form's spacing.
          <RegistrationTermsDisclaimer
            className="-mt-1"
            onMouseDown={(event) => event.preventDefault()}
          />
        )}
      </Form>
      <OrgNewsletterCheckbox className="ml-1" />
      <AuthProviderButtons />
    </>
  )
}
